import {
  HomeIcon,
  UsersIcon,
  FileTextIcon,
  SendIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  PlusCircleIcon,
  SearchIcon,
  BarChart2Icon,
  ShieldIcon,
  ClipboardListIcon,
  ClipboardCheckIcon,
  FlagIcon,
  GraduationCapIcon,
  ListChecksIcon,
  TargetIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * The app shell's navigation (#358). Items, names, hrefs and role gating are
 * carried over unchanged from the old UnifiedNavbar (staff) and TeacherNavbar
 * (teacher). Gating here only hides links; every route keeps its own guard in
 * App.tsx (FullAccessRoute etc.), which is the real boundary.
 */

export interface NavAccess {
  canAccessDocuments: boolean;
  isAdmin: boolean;
  isKeystoneMember: boolean;
  isKeystoneLeader: boolean;
}

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  requiresFullAccess?: boolean; // Hidden from LD Assistants
  requiresLeader?: boolean; // Hidden unless Keystone Leader / admin
}

export interface NavModule {
  id: string;
  name: string;
  icon: LucideIcon;
  /** Where the rail entry goes. For sections, the first visible item. */
  href: string;
  /** Sub-pages, shown as the section tabs under the top bar. */
  items?: NavItem[];
  /** Pinned to the foot of the rail (and the phone "More" menu). */
  foot?: boolean;
  isActive: (pathname: string) => boolean;
}

const startsWith = (base: string) => (p: string) => p === base || p.startsWith(`${base}/`);

const DOCUMENT_ITEMS: NavItem[] = [
  { name: 'Dashboard', href: '/staff/documents', icon: HomeIcon },
  { name: 'All Documents', href: '/staff/documents/list', icon: FileTextIcon },
  { name: 'Ready to Send', href: '/staff/documents/ready', icon: SendIcon },
  { name: 'Signed Documents', href: '/staff/documents/signed', icon: CheckCircleIcon },
  { name: 'Document Tracking', href: '/staff/documents/tracking', icon: ClockIcon },
];

const CASE_NOTE_ITEMS: NavItem[] = [
  { name: 'Quick Add', href: '/staff/case-notes', icon: PlusCircleIcon },
  { name: 'Search Notes', href: '/staff/case-notes/search', icon: SearchIcon },
  { name: 'By Student', href: '/staff/case-notes/students', icon: UsersIcon },
  { name: 'Flagged', href: '/staff/case-notes/flagged', icon: FlagIcon, requiresFullAccess: true },
  { name: 'Analytics', href: '/staff/case-notes/analytics', icon: BarChart2Icon, requiresFullAccess: true },
  { name: 'Admin', href: '/staff/case-notes/admin', icon: ShieldIcon, adminOnly: true },
];

const KEYSTONE_ITEMS: NavItem[] = [
  { name: 'Manage Enrolment', href: '/staff/keystone', icon: ListChecksIcon },
  { name: 'Eligibility', href: '/staff/keystone/eligibility', icon: TargetIcon, requiresLeader: true },
  { name: 'Notes', href: '/staff/keystone/notes', icon: ClipboardListIcon },
  { name: 'Analytics', href: '/staff/keystone/analytics', icon: BarChart2Icon },
  { name: 'Settings', href: '/staff/keystone/settings', icon: CogIcon, requiresLeader: true },
];

export function visibleItems(items: NavItem[], access: NavAccess): NavItem[] {
  return items.filter((item) => {
    if (item.adminOnly && !access.isAdmin) return false;
    if (item.requiresFullAccess && !access.canAccessDocuments) return false;
    if (item.requiresLeader && !access.isKeystoneLeader && !access.isAdmin) return false;
    return true;
  });
}

/** Dashboards are exact matches; every other sub-page also owns its children. */
export function isItemActive(href: string, pathname: string): boolean {
  if (href === '/staff/documents' || href === '/staff/case-notes' || href === '/staff/keystone') {
    return pathname === href;
  }
  return startsWith(href)(pathname);
}

function section(id: string, name: string, icon: LucideIcon, base: string, items: NavItem[], access: NavAccess): NavModule {
  const shown = visibleItems(items, access);
  return { id, name, icon, href: shown[0]?.href ?? base, items: shown, isActive: startsWith(base) };
}

export function staffModules(access: NavAccess): NavModule[] {
  // A keystone_leader who is not also an admin works exclusively in Keystone:
  // Case Notes and Documents are hidden. (Master List is gated on
  // canAccessDocuments only, exactly as before.)
  const hideStandardModules = access.isKeystoneLeader && !access.isAdmin;
  const modules: NavModule[] = [
    { id: 'home', name: 'Home', icon: HomeIcon, href: '/staff/home', isActive: (p) => p === '/staff/home' },
    { id: 'students', name: 'Students', icon: UsersIcon, href: '/staff/students', isActive: startsWith('/staff/students') },
  ];
  // Master List: gated like its route (FullAccessRoute).
  if (access.canAccessDocuments) {
    modules.push({ id: 'master-list', name: 'Master List', icon: ClipboardListIcon, href: '/staff/master-list', isActive: (p) => p === '/staff/master-list' });
  }
  if (!hideStandardModules) {
    modules.push(section('case-notes', 'Case Notes', ClipboardListIcon, '/staff/case-notes', CASE_NOTE_ITEMS, access));
    if (access.canAccessDocuments) {
      modules.push(section('documents', 'Documents', FileTextIcon, '/staff/documents', DOCUMENT_ITEMS, access));
    }
  }
  if (access.isKeystoneMember) {
    modules.push(section('keystone', 'Keystone', GraduationCapIcon, '/staff/keystone', KEYSTONE_ITEMS, access));
  }
  if (access.isAdmin) {
    modules.push({ id: 'settings', name: 'Settings', icon: CogIcon, href: '/staff/settings', foot: true, isActive: startsWith('/staff/settings') });
  }
  return modules;
}

export function teacherModules(): NavModule[] {
  return [
    { id: 'teacher-dashboard', name: 'Dashboard', icon: ClipboardCheckIcon, href: '/teacher', isActive: startsWith('/teacher') },
  ];
}
